import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  Image as ChakraImage,
  Link,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import { Base64 } from 'js-base64'
import _ from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegBell } from 'react-icons/fa'
import { HiMiniBookmark } from 'react-icons/hi2'
import { IoCloseOutline, IoImages, IoLogOutOutline } from 'react-icons/io5'
import { MdCollections } from 'react-icons/md'
import { TbLink } from 'react-icons/tb'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import type { ViewableParentSuccessResponse } from '../../interfaces'
import { MLContext, UserContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import BookmarksDrawer from '../NavBar/BookmarksDrawer'
import SavedSearchesDrawer from '../NavBar/SavedSearchesDrawer'
import { useMLWidgetContext } from '../../contexts/MLWidgetContext'
import NotificationDrawer from './Notifications/NotificationDrawer'
import Search from './Search'

interface Props {
  resetSearch(): void
}

const NotificationBell = ({
  count,
  onOpenNotificationDrawer,
}: {
  count: number
  onOpenNotificationDrawer(): void
}): React.ReactElement => {
  const displayCount = count > 99 ? '99+' : `${count}`
  return (
    <IconButton
      onClick={onOpenNotificationDrawer}
      variant="link"
      css={css`
        position: relative !important;
      `}
      aria-label="Notifications"
      colorScheme="appSlate"
      height="35px"
      width="35px"
      py={2}
      icon={
        <>
          <FaRegBell color="gray.750" size="18px" />
          {count > 0 ? (
            <Box
              minW="18px"
              color="white"
              position="absolute"
              top="2px"
              right="2px"
              fontSize="0.6rem"
              bgColor="red"
              borderRadius="lg"
              zIndex={9999}
              p="1px"
            >
              {displayCount}
            </Box>
          ) : null}
        </>
      }
    />
  )
}

const NoitificationBellMemoized = React.memo(NotificationBell)

const HeaderActionbar = ({ resetSearch }: Props): React.ReactElement | null => {
  const [
    {
      currentMediaCollection,
      folderPath,
      viewableParent,
      mediaCollectionRoute,
      unseenNotificationsCount,
    },
    dispatch,
  ] = useContext(MLContext)
  const { t } = useTranslation()
  const {
    dashboardServerURL: directDashboardServerURL,
    isPublicLink,
    urlPrefix,
  } = useContext(PublicLinksInfoContext)

  const history = useHistory()
  const location = useLocation()

  const {
    url,
    params: { folderPathBase64 },
  } = useRouteMatch<{ folderPathBase64?: string }>()

  const [searchFocused, setSearchFocused] = useState<boolean>(false)

  const [mlWidgetCloseButtonHidden, setMlWidgetCloseButtonHidden] =
    useState<boolean>(false)

  const {
    isOpen: isOpenNotificationDrawer,
    onOpen: onOpenNotificationDrawer,
    onClose: onCloseNotificationDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenBookmarks,
    onClose: onCloseBookmarks,
    onOpen: onOpenBookmarks,
  } = useDisclosure()

  const {
    isOpen: isOpenSavedSearches,
    onClose: onCloseSavedSearches,
    onOpen: onOpenSavedSearches,
  } = useDisclosure()

  useEffect(() => {
    dispatch([
      {
        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
    ])
    setSearchFocused(false)
  }, [
    folderPath,
    currentMediaCollection,
    url,
    folderPathBase64,
    location.search,
  ])

  const { settings } = useMLWidgetContext()

  useEffect(() => {
    // Toolbar settings for widget
    if (
      settings?.toolbar &&
      typeof settings.toolbar.showCloseButton !== 'undefined'
    ) {
      setMlWidgetCloseButtonHidden(!settings.toolbar.showCloseButton)
    }
  }, [settings])

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('ik-search-bar-focused', {
        detail: searchFocused,
      }),
    )
  }, [searchFocused])

  return (
    <>
      <HStack
        width="full"
        spacing="10"
        justifyContent="space-between"
        position="relative"
      >
        {!isPublicLink ? (
          <Box width="100%">
            <Search
              searchFocused={searchFocused}
              setSearchFocused={setSearchFocused}
              resetSearch={resetSearch}
            />
          </Box>
        ) : (
          <Box width="50%">
            <ChakraImage
              src="https://ik.imagekit.io/ikmedia/logo/light_T4buIzohVH.svg"
              alt="ImageKit logo"
              height="35px"
            />
          </Box>
        )}

        {(viewableParent as ViewableParentSuccessResponse).asset ||
        (viewableParent as ViewableParentSuccessResponse).mediaCollection ? (
          <VStack
            alignItems="end"
            position="absolute"
            right="0"
            top="0"
          >
            <HStack spacing="6">
              {!isPublicLink && (
                <NoitificationBellMemoized
                  count={unseenNotificationsCount}
                  onOpenNotificationDrawer={onOpenNotificationDrawer}
                />
              )}
              {window.isMediaLibraryWidget ? (
                <HStack spacing="2">
                  {mediaCollectionRoute ? (
                    <Tooltip
                      label={t('go_to_media_library', 'Go to media library')}
                    >
                      <Button
                        size="sm"
                        leftIcon={<IoImages />}
                        onClick={() => {
                          history.push('/media-library')
                        }}
                      >
                        {t('media_library', 'Media Library')}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      label={t(
                        'go_to_media_collection',
                        'Go to media collection',
                      )}
                    >
                      <Button
                        size="sm"
                        leftIcon={<MdCollections />}
                        onClick={() => {
                          history.push('/media-collections')
                        }}
                      >
                        {t('media_collection', 'Media collection')}
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip label={t('saved_searches', 'Saved searches')}>
                    <IconButton
                      size="sm"
                      aria-label={t('saved_searches', 'Saved searches')}
                      icon={<HiMiniBookmark />}
                      onClick={() => {
                        onCloseBookmarks()

                        if (isOpenSavedSearches) {
                          onCloseSavedSearches()
                        } else {
                          onOpenSavedSearches()
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip label={t('bookmarks', 'Bookmarks')}>
                    <IconButton
                      size="sm"
                      aria-label={t('bookmarks', 'Bookmarks')}
                      icon={<TbLink />}
                      onClick={() => {
                        onCloseSavedSearches()

                        if (isOpenBookmarks) {
                          onCloseBookmarks()
                        } else {
                          onOpenBookmarks()
                        }
                      }}
                    />
                  </Tooltip>
                  <ButtonGroup size="sm" isAttached>
                    <Button
                      colorScheme="appSlate"
                      leftIcon={<Icon as={IoLogOutOutline} />}
                      fontWeight="normal"
                      borderRightRadius="0"
                      as={Link}
                      href={`/logout?isMediaLibraryWidget=${
                        window.isMediaLibraryWidget
                      }${
                        window.widgetHost
                          ? `&widgetHost=${window.widgetHost}`
                          : ''
                      }${
                        window.isMediaLibraryWidget
                          ? '&redirectTo=media-library-widget'
                          : ''
                      }${
                        !_.isEmpty(settings?.initialView)
                          ? `&mlWidgetInitialView=${Base64.encode(
                              JSON.stringify(settings?.initialView),
                            )}`
                          : ''
                      }${
                        window.widgetImagekitId
                          ? `&widgetImagekitId=${window.widgetImagekitId}`
                          : ''
                      }
                          `}
                    >
                      {t('logout', 'Logout')}
                    </Button>
                    <Button
                      display={mlWidgetCloseButtonHidden ? 'none' : 'block'}
                      colorScheme="appSlate"
                      leftIcon={<Icon as={IoCloseOutline} />}
                      fontWeight="normal"
                      variant="outline"
                      borderLeftRadius="0"
                      onClick={() => {
                        if (window.self !== window.top) {
                          window.parent.postMessage(
                            { eventType: 'CLOSE_MEDIA_LIBRARY_WIDGET' },
                            window.widgetHost ?? document.referrer,
                          )
                        } else {
                          console.log(
                            '[DEBUG]',
                            'Simulating response to parent',
                            {
                              eventType: 'CLOSE_MEDIA_LIBRARY_WIDGET',
                              widgetHost:
                                window.widgetHost ?? document.referrer,
                            },
                          )
                        }
                      }}
                    >
                      {t('close', 'Close')}
                    </Button>
                  </ButtonGroup>
                </HStack>
              ) : null}
            </HStack>
          </VStack>
        ) : null}
      </HStack>
      {!isPublicLink && isOpenNotificationDrawer && (
        <NotificationDrawer
          isOpen={isOpenNotificationDrawer}
          onClose={onCloseNotificationDrawer}
        />
      )}

      {isOpenSavedSearches && (
        <SavedSearchesDrawer
          isOpenSavedSearches={isOpenSavedSearches}
          onCloseSavedSearches={onCloseSavedSearches}
        />
      )}
      {isOpenBookmarks && (
        <BookmarksDrawer
          isOpenBookmarks={isOpenBookmarks}
          onCloseBookmarks={onCloseBookmarks}
        />
      )}
    </>
  )
}

export default HeaderActionbar
